import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { shortDate, todayInput } from '../lib/format';
import { haptic } from '../lib/telegram';
import type { Schedule } from '../types';
import { Button, Card, ErrorNotice, Field, Input } from './ui';

/** Короткие имена дней: неделя должна помещаться в строку на телефоне. */
const WEEKDAYS = [
  { day: 1, label: 'Пн' },
  { day: 2, label: 'Вт' },
  { day: 3, label: 'Ср' },
  { day: 4, label: 'Чт' },
  { day: 5, label: 'Пт' },
  { day: 6, label: 'Сб' },
  { day: 7, label: 'Вс' },
];

/**
 * Прогноз загрузки: когда художник реально возьмётся за каждый заказ и когда
 * освободится. Считается на сервере из очереди, оценок, рабочих дней и отпусков
 * — и нигде не сохраняется: он меняется от любой перестановки очереди.
 *
 * Заказчику эти даты не уходят. Он видит только срок, который художник назначил
 * сам и который заказчик согласовал.
 */
export function WorkloadForecast() {
  const queryClient = useQueryClient();
  const [startsOn, setStartsOn] = useState(todayInput());
  const [endsOn, setEndsOn] = useState(todayInput());
  const [comment, setComment] = useState('');
  const [adding, setAdding] = useState(false);

  const schedule = useQuery({
    queryKey: ['schedule'],
    queryFn: () => api<Schedule>('/artist/schedule'),
  });
  const workingDays = useMutation({
    mutationFn: (days: number[]) =>
      api('/artist/profile', { method: 'PATCH', body: { workingDays: days } }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
  const addTimeOff = useMutation({
    mutationFn: () =>
      api<Schedule>('/artist/schedule/time-off', {
        method: 'POST',
        body: { startsOn, endsOn, comment: comment.trim() || null },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['schedule'], updated);
      setComment('');
      setAdding(false);
    },
  });
  const removeTimeOff = useMutation({
    mutationFn: (id: string) =>
      api<Schedule>(`/artist/schedule/time-off/${id}`, { method: 'DELETE' }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['schedule'], updated);
    },
  });

  const data = schedule.data;
  const risky = data?.items.filter((item) => item.atRisk) ?? [];

  const toggleDay = (day: number) => {
    if (!data) return;
    const next = data.workingDays.includes(day)
      ? data.workingDays.filter((value) => value !== day)
      : [...data.workingDays, day].sort();

    // Неделя без единого рабочего дня превратила бы прогноз в «никогда».
    if (next.length === 0) return;
    workingDays.mutate(next);
  };

  return (
    <>
      <div className="section-heading">
        <h2>Когда я освобожусь</h2>
      </div>

      {schedule.error && <ErrorNotice error={schedule.error} />}

      <Card className="stack stack--compact">
        <div className="card-row">
          <span>Свободен с</span>
          <strong>{data?.nextOpening ? shortDate(data.nextOpening) : '—'}</strong>
        </div>
        <p className="muted">
          По очереди, оценкам, рабочим дням и отпускам. Заказчику не показывается.
        </p>
        {data && data.withoutEstimate > 0 && (
          <p className="meta">
            {data.withoutEstimate} заказ(ов) без оценки — они в прогноз не вошли.
          </p>
        )}
      </Card>

      {risky.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Не успеваем</h2>
            <span className="meta">{risky.length}</span>
          </div>
          <div className="list">
            {risky.map((item) => (
              <Link
                key={item.id}
                href={`/orders/${item.id}`}
                className="card attention-row attention-row--danger"
              >
                <span className="attention-row__marker">!</span>
                <span className="attention-row__content">
                  <strong>
                    {item.title} — опоздание на {item.overdueBy} дн.
                  </strong>
                  <small>
                    {item.clientName} · срок {shortDate(item.deadline)} · освободится{' '}
                    {shortDate(item.plannedEndAt)}
                  </small>
                </span>
                <span className="chevron" aria-hidden="true">
                  ›
                </span>
              </Link>
            ))}
          </div>
        </>
      )}

      <div className="section-heading">
        <h2>Рабочие дни</h2>
      </div>
      <Card className="stack stack--compact">
        <div className="weekday-row">
          {WEEKDAYS.map((weekday) => (
            <button
              key={weekday.day}
              type="button"
              className={`weekday ${data?.workingDays.includes(weekday.day) ? 'is-on' : ''}`}
              aria-pressed={data?.workingDays.includes(weekday.day) ?? false}
              disabled={workingDays.isPending || !data}
              onClick={() => toggleDay(weekday.day)}
            >
              {weekday.label}
            </button>
          ))}
        </div>
        <p className="muted settings-note">В нерабочие дни прогноз работу не планирует.</p>

        {workingDays.error && <ErrorNotice error={workingDays.error} />}
      </Card>

      <div className="section-heading">
        <h2>Отпуск и перерывы</h2>
        <button onClick={() => setAdding((value) => !value)}>
          {adding ? 'Отмена' : 'Добавить'}
        </button>
      </div>
      <Card className="stack stack--compact">
        {data?.timeOff.length === 0 && !adding && (
          <p className="muted">Отметьте даты отпуска — прогноз сдвинется.</p>
        )}

        {data?.timeOff.map((period) => (
          <div key={period.id} className="card-row">
            <div>
              <strong>
                {shortDate(period.startsOn)} — {shortDate(period.endsOn)}
              </strong>
              {period.comment && <p className="meta">{period.comment}</p>}
            </div>
            <button
              type="button"
              className="link-button"
              disabled={removeTimeOff.isPending}
              onClick={() => removeTimeOff.mutate(period.id)}
            >
              Убрать
            </button>
          </div>
        ))}

        {adding && (
          <form
            // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
            noValidate
            className="stack stack--compact"
            onSubmit={(event) => {
              event.preventDefault();
              addTimeOff.mutate();
            }}
          >
            <div className="form-grid">
              <Field label="С">
                <Input
                  type="date"
                  value={startsOn}
                  onChange={(event) => setStartsOn(event.target.value)}
                />
              </Field>
              <Field label="По" error={endsOn < startsOn ? 'Раньше начала' : undefined}>
                <Input
                  type="date"
                  min={startsOn}
                  value={endsOn}
                  onChange={(event) => setEndsOn(event.target.value)}
                />
              </Field>
            </div>
            <Field label="Заметка" hint="Необязательно. Видно только вам">
              <Input
                value={comment}
                placeholder="Отпуск"
                onChange={(event) => setComment(event.target.value)}
              />
            </Field>
            {addTimeOff.error && <ErrorNotice error={addTimeOff.error} />}
            <Button type="submit" disabled={endsOn < startsOn || addTimeOff.isPending}>
              {addTimeOff.isPending ? 'Сохраняем…' : 'Отметить перерыв'}
            </Button>
          </form>
        )}

        {removeTimeOff.error && <ErrorNotice error={removeTimeOff.error} />}
      </Card>
    </>
  );
}
